/**
 * Player Settings — MAC, key, version, update check, external player placeholder, logout/reset.
 */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { useDevice } from "../lib/DeviceContext";
import { checkForUpdates } from "../lib/playerApi";
import { clearAllPlaylistCaches } from "../lib/playlistCache";
import { clearImageCache, getImageCacheStats } from "../lib/imageCache";
import { clearHistory } from "../lib/watchHistory";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import {
  clearTmdbCache,
  getCacheStats,
} from "../lib/tmdbCache";
import {
  startScan,
  cancelScan,
  getScanState,
  subscribeToScan,
} from "../lib/metadataScanner";
import { getSelectedEngine, setSelectedEngine } from "../lib/nativePlayer";
import {
  isAutoPlayNextEpisodeEnabled,
  setAutoPlayNextEpisodeEnabled,
} from "../lib/autoPlayPref";
import { APP_VERSION } from "../lib/version";
import { TILE_SIZES, getTileSize, setTileSize, gridClassesFor } from "../lib/tileSize";
import ScreenSizeSetup from "../components/ScreenSizeSetup";
import { isDebugEnabled, enableDebug, disableDebug } from "../lib/debugMode";
import { checkConnectionLimit } from "../lib/connectionLimit";
import {
  canInstallApkInApp,
  startApkDownload,
  cancelApkDownload,
  installDownloadedApk,
} from "../lib/apkUpdater";
import SpeedTest from "../components/SpeedTest";
import AccountInfo from "../components/AccountInfo";
// v1.0.9 — Movie Cue intermission settings.
import {
  getIntermissionMinutes,
  setIntermissionMinutes,
  INTERMISSION_OPTIONS,
} from "../lib/movieCue";
import { Popcorn } from "lucide-react";
import {
  ArrowLeft,
  Copy,
  ListVideo,
  RefreshCcw,
  LogOut,
  Cpu,
  Trash2,
  Loader2,
  CheckCircle2,
  ExternalLink,
  Sparkles,
  Database,
  Bug,
  Download,
  XCircle,
  EyeOff,
  History as HistoryIcon,
  AlertOctagon,
} from "lucide-react";

const ENGINE_BUCKETS = [
  {
    key: "live",
    title: "Live TV",
    blurb:
      "Default: Media3 / ExoPlayer. If a channel plays silent video or won't start (common with AC3/EAC3 Dolby audio), switch this bucket to VLC.",
    defaultEngine: "exoplayer",
  },
  {
    key: "movie",
    title: "Movies",
    blurb:
      "VOD movies are usually plain H.264 / H.265 + AAC. ExoPlayer starts faster and uses less battery here.",
    defaultEngine: "exoplayer",
  },
  {
    key: "series",
    title: "Series",
    blurb:
      "Episode playback behaves like Movies — ExoPlayer first, switch to VLC if a show's audio refuses to play.",
    defaultEngine: "exoplayer",
  },
];

function readBucketEngine(key) {
  if (typeof window === "undefined") {
    return ENGINE_BUCKETS.find((b) => b.key === key)?.defaultEngine || "exoplayer";
  }
  return getSelectedEngine(
    key === "live" ? "live_tv" : key === "series" ? "series" : "movie"
  );
}

export default function PlayerSettings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { playlists: playlistsCtx } = useOutletContext() || {};
  const { deviceId, activationKey, device, logout, factoryReset } = useDevice();
  // v1.3.35 — Watch.jsx redirects the user here with this state flag set
  // when the Xtream subscription has hit its `max_connections` cap. We
  // surface a red banner at the very top of the Settings page so the
  // user immediately sees WHY their stream didn't start.
  //
  // v1.3.44 — the redirect payload is a *snapshot* from the moment Watch
  // ran its pre-flight. By the time the user is staring at this banner,
  // another device may have already disconnected — at which point the
  // banner says "3 streaming" while the Account Info card right below
  // says "0 active". Confusing as hell. So: on mount, if the state flag
  // is set, re-run checkConnectionLimit(). If it now returns ok=true we
  // suppress the banner entirely. We also wipe location.state once
  // dismissed so a refresh / re-navigation doesn't bring it back.
  const [limitInfo, setLimitInfo] = useState(() =>
    location.state?.connectionLimitReached
      ? {
          active: location.state.active,
          max: location.state.max,
          providerName: location.state.providerName,
        }
      : null,
  );

  useEffect(() => {
    if (!limitInfo) return undefined;
    let cancelled = false;
    (async () => {
      const res = await checkConnectionLimit();
      if (cancelled) return;
      if (res.ok) {
        // No longer over the limit — clear banner + scrub location.state
        // so a refresh doesn't bring the stale message back.
        setLimitInfo(null);
        try {
          navigate(location.pathname, { replace: true, state: null });
        } catch { /* ignore */ }
      } else {
        // Still over — update with fresh numbers so the banner matches
        // the Account Info card on this same page.
        setLimitInfo({
          active: res.active,
          max: res.max,
          providerName: res.providerName,
        });
      }
    })();
    return () => { cancelled = true; };
    // We deliberately only re-validate on first mount; subsequent
    // dismissal is permanent for this Settings visit.
  }, []);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  // In-app APK update state machine: idle → downloading → ready → installing
  // (or "error" if anything blew up). We keep the downloaded `path` so the
  // Install button knows what to hand off to the system package installer.
  const [updatePhase, setUpdatePhase] = useState("idle");
  const [updateProgress, setUpdateProgress] = useState({ percent: 0, downloaded: 0, total: 0 });
  const [updatePath, setUpdatePath] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  // v1.0.9 — Movie Cue intermission length picker (5/10/15 min).
  const [intermissionMin, setIntermissionMinState] = useState(() =>
    getIntermissionMinutes(),
  );
  const [engines, setEngines] = useState(() => ({
    live: readBucketEngine("live"),
    movie: readBucketEngine("movie"),
    series: readBucketEngine("series"),
  }));

  const selectEngine = (bucket, next) => {
    setSelectedEngine(bucket, next);
    setEngines((prev) => ({ ...prev, [bucket]: next }));
  };

  const copy = (text) => navigator.clipboard.writeText(text);

  const onCheckUpdates = async () => {
    setChecking(true);
    try {
      const info = await checkForUpdates();
      setUpdateInfo(info);
      // Reset the in-app download state-machine whenever we re-check —
      // a fresh check means the user wants a fresh download.
      setUpdatePhase("idle");
      setUpdateProgress({ percent: 0, downloaded: 0, total: 0 });
      setUpdatePath(null);
      setUpdateError(null);
    } catch {
      setUpdateInfo({ error: "Could not check for updates" });
    } finally {
      setChecking(false);
    }
  };

  // Resolve the update URL into an absolute one — the admin panel may
  // store it as a relative `/api/downloads/foo.apk` path or as a full
  // https:// URL. Capacitor's HttpURLConnection wants an absolute URL.
  const absoluteUpdateUrl = (raw) => {
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (raw.startsWith("/")) return base + raw;
    return base + "/" + raw;
  };

  const onStartDownload = async () => {
    const raw = updateInfo?.download_url;
    const url = absoluteUpdateUrl(raw);
    if (!url) return;
    setUpdatePhase("downloading");
    setUpdateError(null);
    setUpdateProgress({ percent: 0, downloaded: 0, total: 0 });
    try {
      await startApkDownload({
        url,
        onProgress: (ev) => {
          setUpdateProgress({
            percent: typeof ev.percent === "number" ? ev.percent : 0,
            downloaded: ev.downloaded || 0,
            total: ev.total || 0,
          });
        },
        onDone: (ev) => {
          setUpdatePath(ev?.path || null);
          setUpdateProgress((p) => ({ ...p, percent: 100 }));
          setUpdatePhase("ready");
        },
        onError: (err) => {
          setUpdateError(err?.message || String(err));
          setUpdatePhase("error");
        },
      });
    } catch (err) {
      setUpdateError(err?.message || String(err));
      setUpdatePhase("error");
    }
  };

  const onCancelDownload = async () => {
    await cancelApkDownload();
    setUpdatePhase("idle");
    setUpdateProgress({ percent: 0, downloaded: 0, total: 0 });
  };

  const onInstallNow = async () => {
    setUpdatePhase("installing");
    setUpdateError(null);
    try {
      await installDownloadedApk(updatePath);
      // The package installer takes over from here. If the user backs
      // out we leave the phase at "installing" — the Settings screen is
      // about to be replaced by the OS chrome anyway.
      // Fallback: after 8s, if we're still visible (installer didn't
      // launch — likely permission denied), reset to "ready" so the
      // user can tap Install again after granting the permission.
      setTimeout(() => {
        setUpdatePhase((cur) => (cur === "installing" ? "ready" : cur));
      }, 8000);
    } catch (err) {
      setUpdateError(err?.message || String(err));
      // Back to "ready" (not "error") so the Install button reappears
      // — the user has just granted the permission (settings opened
      // for them) and only needs to tap Install again.
      setUpdatePhase("ready");
    }
  };

  const fmtBytes = (n) => {
    if (!n || n < 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleLogout = () => {
    if (window.confirm("Sign out and remove the activation key from this device?")) {
      logout();
      navigate("/player/activate", { replace: true });
    }
  };

  const handleReset = () => {
    if (
      window.confirm(
        "Factory reset: this clears your activation AND device ID. You'll need a new activation. Continue?"
      )
    ) {
      factoryReset();
    }
  };

  const handleClearCache = async () => {
    if (
      !window.confirm(
        "Clear cached playlists and EPG?\n\nThis frees up storage. The next time you open Live TV / Movies / Series the player will re-fetch everything fresh from your provider (may take a few seconds)."
      )
    )
      return;
    setClearingCache(true);
    try {
      await clearAllPlaylistCaches();
      // Also drop the watch-history bucket since it lives off the same cache.
      window.alert("Cache cleared. Reopen a section to reload your playlist.");
    } finally {
      setClearingCache(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <button
        onClick={() => navigate("/player/menu")}
        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Menu
      </button>

      <h2 className="text-xl font-bold">Settings</h2>

      {/* v1.3.35 — Max-connections banner. Shown only when Watch.jsx
          redirected the user here because the Xtream subscription was at
          its concurrent-device cap. Auto-clears when the user reloads
          the Settings tab (location.state resets on next navigation). */}
      {limitInfo && (
        <section
          data-testid="settings-conn-limit-banner"
          className="bg-red-950/60 border border-red-500/60 rounded-2xl p-5 flex items-start gap-3"
        >
          <AlertOctagon className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold text-red-100">
              Max connections limit reached
            </div>
            <div className="text-sm text-red-200/90 mt-1 leading-relaxed">
              Your subscription
              {limitInfo.providerName ? ` (${limitInfo.providerName})` : ""}
              {" "}allows a maximum of{" "}
              <span className="font-semibold">{limitInfo.max}</span>{" "}
              simultaneous device{limitInfo.max === 1 ? "" : "s"}, and{" "}
              <span className="font-semibold">{limitInfo.active}</span>{" "}
              {limitInfo.active === 1 ? "is" : "are"} already streaming.
              Close another device or wait a moment, then try again.
            </div>
          </div>
        </section>
      )}

      {/* Device info */}
      <section className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Device</h3>

        <Row label="MAC / Device ID" value={deviceId} onCopy={() => copy(deviceId)} />
        <Row label="Activation key" value={activationKey || "—"} onCopy={activationKey ? () => copy(activationKey) : null} mono />
        <Row label="Platform" value={device?.platform || "web"} />
        <Row label="App version" value={device?.app_version || APP_VERSION} />

        {/* v1.4.23 — Restored the app-update UI. The `onCheckUpdates`
            handler + download/install state machine existed since
            v1.3.x but the render tree that hooked them up was
            accidentally removed during the Settings redesign. Users
            reported "where is the update button?" — this card brings
            it back. */}
        <div className="pt-3 mt-3 border-t border-white/5" data-testid="settings-app-update-card">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#f50261]/15 flex items-center justify-center shrink-0">
              <Download className="w-4 h-4 text-[#ff4d9d]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white">App update</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                Check the ZA Player Pro update server for a newer build. On Android/TV the new APK downloads in the app and installs on tap.
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  data-testid="settings-check-updates-btn"
                  onClick={onCheckUpdates}
                  disabled={updatePhase === "downloading" || updatePhase === "installing"}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/15 border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCcw className="w-3.5 h-3.5" />
                  Check for updates
                </button>
                {updateInfo && !updateInfo.error && updateInfo.has_update && updatePhase === "idle" && (
                  <button
                    data-testid="settings-download-update-btn"
                    onClick={onStartDownload}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#f50261] hover:bg-[#ff2b7e] text-white transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download v{updateInfo.latest_version || "?"}
                  </button>
                )}
                {updatePhase === "downloading" && (
                  <>
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Downloading {updateProgress.percent}% ({fmtBytes(updateProgress.downloaded)}/{fmtBytes(updateProgress.total)})
                    </span>
                    <button
                      data-testid="settings-cancel-update-btn"
                      onClick={onCancelDownload}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  </>
                )}
                {updatePhase === "ready" && (
                  <button
                    data-testid="settings-install-update-btn"
                    onClick={onInstallNow}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Install now
                  </button>
                )}
                {updatePhase === "installing" && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Launching installer…
                  </span>
                )}
              </div>
              {updateInfo?.error && (
                <div className="mt-2 text-[11px] text-rose-400">{updateInfo.error}</div>
              )}
              {updateInfo && !updateInfo.error && !updateInfo.has_update && updatePhase === "idle" && (
                <div className="mt-2 text-[11px] text-emerald-400">
                  You are on the latest version (v{APP_VERSION}).
                </div>
              )}
              {updateError && (
                <div className="mt-2 text-[11px] text-rose-400">{updateError}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* v1.0.9 — Movie Cue: cinema intermission between features. */}
      <section
        data-testid="settings-movie-cue-card"
        className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-rose-500/15 flex items-center justify-center shrink-0">
            <Popcorn className="w-4 h-4 text-rose-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
              Movie Cue intermission
            </h3>
            <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">
              When you play back-to-back with the Movie Cue, this is the
              cinema-style break shown between features. Pick something
              short for TV binge sessions or long for a real intermission.
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {INTERMISSION_OPTIONS.map((n) => (
                <button
                  key={n}
                  data-testid={`intermission-btn-${n}`}
                  onClick={() => {
                    setIntermissionMinutes(n);
                    setIntermissionMinState(n);
                  }}
                  className={
                    "px-3 py-1.5 rounded-md text-xs font-medium border transition-colors " +
                    (intermissionMin === n
                      ? "bg-rose-500 border-rose-500 text-white"
                      : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10")
                  }
                >
                  {n} min
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* v1.4.30 — Playback engine picker (Android only). Users can
          override the default per content-type when a channel/movie/
          episode's audio/video codec ExoPlayer cannot decode; switching
          the bucket to VLC uses the bundled libVLC engine instead. */}
      <section
        data-testid="settings-playback-engine-card"
        className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#f50261]/15 flex items-center justify-center shrink-0">
            <Cpu className="w-4 h-4 text-[#ff4d9d]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
              Playback engine (Android)
            </h3>
            <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">
              Choose which native player handles each type of stream. Defaults
              to Media3 / ExoPlayer. Switch a bucket to VLC when the stream&apos;s
              audio plays silent, the picture won&apos;t start, or the file uses an
              exotic codec ExoPlayer can&apos;t decode.
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {ENGINE_BUCKETS.map((bucket) => (
            <div
              key={bucket.key}
              data-testid={`engine-row-${bucket.key}`}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 p-3 rounded-lg bg-white/5 border border-white/5"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white">
                  {bucket.title}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                  {bucket.blurb}
                </div>
              </div>
              <div className="shrink-0">
                <select
                  data-testid={`engine-select-${bucket.key}`}
                  value={engines[bucket.key]}
                  onChange={(e) => selectEngine(bucket.key, e.target.value)}
                  className="px-3 py-1.5 rounded-md bg-[#0a0d13] border border-white/10 text-sm text-white focus:outline-none focus:border-[#f50261] transition-colors"
                >
                  <option value="exoplayer">Media3 / ExoPlayer</option>
                  <option value="vlc">VLC</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* v1.4.44 — User-controlled cover tile size with live preview. */}
      <TileSizeCard />

      {/* v1.4.45 — Manual whole-app screen-size / zoom slider. */}
      <ScreenSizeCard />

      {/* Auto-play next episode — when on, tapping an episode pre-loads
          the rest of the season into the native player so it rolls over
          to the next file without coming back to this UI. */}
      <AutoPlayNextEpisodeCard />

      {/* Debug mode — anyone can flip this on to start shipping console
          logs + uncaught errors to the admin panel. Useful for reporting
          a problem without having to type the secret "logtest" key. */}
      <DebugModeCard />

      <SpeedTest />

      {/* v1.3.36 — Removed "Report missing metadata" and "EPG status"
          per request. These were power-user diagnostics that confused
          end users; metadata still resolves automatically in the
          background. */}

      {/* Xtream catalog diagnostic — shows the live/vod/series counts
          the Xtream API returned. Lets power users compare against
          competitor apps (vupro etc.) and instantly verify ZA Player Pro
          discovered the same library. */}
      <XtreamCatalogCard />

      {/* Personalisation — Hide categories you don't want to see + reset
          continue-watching history + switch between assigned playlists.
          Mirrors VU Player Pro's Hide Category & Clear History activities. */}
      <section className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
          Personalisation
        </h3>
        {/* Only show the switcher row when the device has 2+ assigned
            playlists — single-playlist users don't need a "switch" affordance.
            Playlists themselves can only be added/removed from the admin
            panel, never from the player UI. */}
        {Array.isArray(playlistsCtx) && playlistsCtx.length > 1 && (
          <button
            data-testid="settings-switch-playlist-btn"
            onClick={() => navigate("/player/playlists")}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-left transition-colors"
          >
            <div className="w-9 h-9 rounded-lg bg-[#f50261]/15 flex items-center justify-center shrink-0">
              <ListVideo className="w-4 h-4 text-[#ff4d9d]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white">Switch playlist</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                Choose between the {playlistsCtx.length} playlists your provider has assigned. (To add or remove a playlist, use the admin panel.)
              </div>
            </div>
            <ArrowLeft className="w-4 h-4 text-slate-500 rotate-180" />
          </button>
        )}
        <button
          data-testid="settings-hide-categories-btn"
          onClick={() => navigate("/player/settings/hide-categories")}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-left transition-colors"
        >
          <div className="w-9 h-9 rounded-lg bg-[#f50261]/15 flex items-center justify-center shrink-0">
            <EyeOff className="w-4 h-4 text-[#ff4d9d]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white">Hide categories</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              Pick the Live / Movies / Series categories you want removed from
              the sidebar on this device.
            </div>
          </div>
          <ArrowLeft className="w-4 h-4 text-slate-500 rotate-180" />
        </button>
        <button
          data-testid="settings-clear-history-btn"
          onClick={() => {
            if (
              window.confirm(
                "Clear your watch history?\n\nThis removes the items shown under Continue Watching. Your favorites, profiles and playlists stay intact."
              )
            ) {
              clearHistory();
              window.alert("Watch history cleared.");
            }
          }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-left transition-colors"
        >
          <div className="w-9 h-9 rounded-lg bg-[#f50261]/15 flex items-center justify-center shrink-0">
            <HistoryIcon className="w-4 h-4 text-[#ff4d9d]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white">Clear watch history</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              Remove every item from Continue Watching. Favorites &amp; profiles
              aren&apos;t touched.
            </div>
          </div>
        </button>
      </section>

      {/* Storage — Clear cache */}
      <section className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
          Storage
        </h3>
        <p className="text-xs text-slate-500 leading-relaxed">
          The player caches your parsed M3U + EPG for 24 hours so subsequent
          launches are instant. Clear it if a channel seems out of date or
          you want to free up space on the device.
        </p>
        <button
          data-testid="settings-clear-cache"
          onClick={handleClearCache}
          disabled={clearingCache}
          className="w-full flex items-center justify-center gap-2 bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/40 text-orange-200 rounded-lg py-2.5 text-sm disabled:opacity-60"
        >
          {clearingCache ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Clearing…
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4" />
              Clear cache (playlists + EPG)
            </>
          )}
        </button>

        {/* Image cache stats + clear */}
        <ImageCacheRow />
      </section>

      {/* Danger zone */}
      <section className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Account</h3>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-2.5 text-sm"
        >
          <LogOut className="w-4 h-4" />
          Sign out (forget activation key)
        </button>
        <button
          onClick={handleReset}
          className="w-full flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg py-2.5 text-sm"
        >
          Factory reset (clear MAC + activation)
        </button>
      </section>
    </div>
  );
}

function Row({ label, value, onCopy, mono }) {
  return (
    <div className="flex items-center gap-3 bg-black/30 border border-white/5 rounded-lg px-3 py-2">
      <div className="text-xs text-slate-500 w-32 shrink-0">{label}</div>
      <div className={`flex-1 truncate ${mono ? "font-mono text-orange-300" : "text-slate-200"} text-sm`}>
        {value}
      </div>
      {onCopy && (
        <button
          onClick={onCopy}
          className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// MetadataScanCard — surfaces the singleton background scanner (see
// `metadataScanner.js`). The scan runs at module scope, so closing this
// page (or even closing the app and reopening it — state is persisted to
// localStorage) does NOT abort the work in progress.
// ---------------------------------------------------------------------------
function useScanState() {
  const [snapshot, setSnapshot] = useState(getScanState);
  useEffect(() => subscribeToScan(setSnapshot), []);
  return snapshot;
}

function MetadataScanCard() {
  const { channels } = usePlaylistData();
  const scan = useScanState();
  const [cacheStats, setCacheStats] = useState(() => getCacheStats());

  // Refresh the cache count whenever the scanner makes progress or stops.
  useEffect(() => {
    setCacheStats(getCacheStats());
  }, [scan.done, scan.running]);

  // Pre-compute the to-be-reported count from the active playlist so the
  // user can see whether the button is even going to send anything BEFORE
  // they tap it. Without this preview, an empty result reads as a bug
  // ("sent stays on 0!") when in reality the playlist's items all already
  // have a tmdb_id.
  const missingPreview = useMemo(() => {
    const seen = new Set();
    let movies = 0;
    let series = 0;
    let movieTotal = 0;
    let seriesTotal = 0;
    for (const ch of channels || []) {
      if (ch.content_type === "movie") {
        movieTotal += 1;
        if (!ch.tmdb_id) {
          const k = `m|${(ch.name || "").trim().toLowerCase()}|${ch.year || ""}`;
          if (!seen.has(k)) {
            seen.add(k);
            movies += 1;
          }
        }
      } else if (ch.content_type === "series") {
        seriesTotal += 1;
        if (!ch.tmdb_id) {
          const name = ch.series_name || ch.name;
          const k = `s|${(name || "").trim().toLowerCase()}|${ch.year || ""}`;
          if (!seen.has(k)) {
            seen.add(k);
            series += 1;
          }
        }
      }
    }
    return { movies, series, total: movies + series, movieTotal, seriesTotal };
  }, [channels]);

  const onStart = () => {
    if (!channels?.length) return;
    startScan(channels);
  };

  const onClearCache = () => {
    clearTmdbCache();
    setCacheStats(getCacheStats());
  };

  return (
    <section className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-400" />
        Report missing metadata
      </h3>
      <p className="text-[11px] text-slate-500 leading-relaxed">
        Some movies &amp; series in your playlist don&apos;t have a TMDB id,
        so the app can&apos;t look up posters / plots automatically. Instead
        of every TV hammering TMDB on its own, tap the button below to send
        the list of missing items to your provider&apos;s admin panel — they
        can match them once and every device benefits.
      </p>

      {/* Pre-flight preview so the user knows what tapping the button will
          actually send. If this reads "0 items missing" then everything in
          their playlist already has a tmdb_id — nothing to report and the
          counter staying at 0 after a scan is the correct outcome. */}
      <div
        data-testid="settings-metadata-preview"
        className={`text-xs px-3 py-2 rounded-lg border ${
          missingPreview.total > 0
            ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
            : "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
        }`}
      >
        {missingPreview.total > 0 ? (
          <>
            <b>{missingPreview.total.toLocaleString()}</b> unique title
            {missingPreview.total === 1 ? "" : "s"} missing a TMDB id —{" "}
            {missingPreview.movies.toLocaleString()} movies +{" "}
            {missingPreview.series.toLocaleString()} series.
          </>
        ) : missingPreview.movieTotal + missingPreview.seriesTotal === 0 ? (
          <>No movies or series detected in the active playlist.</>
        ) : (
          <>
            All {(missingPreview.movieTotal + missingPreview.seriesTotal).toLocaleString()} movies
            &amp; series in your playlist already have a TMDB id ✓ — nothing
            to report. Your provider already has the metadata your app
            needs.
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <button
          data-testid="settings-metadata-scan"
          onClick={scan.running ? cancelScan : onStart}
          disabled={!scan.running && !channels?.length}
          className={`font-semibold text-sm px-3.5 py-1.5 rounded-md flex items-center gap-1.5 ${
            scan.running
              ? "bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-200"
              : "bg-amber-500 hover:bg-amber-600 text-black disabled:opacity-60"
          }`}
        >
          {scan.running ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Cancel
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Report missing items
            </>
          )}
        </button>
        <button
          data-testid="settings-metadata-clear"
          onClick={onClearCache}
          disabled={scan.running}
          className="border border-white/10 hover:border-white/20 text-slate-300 text-sm px-3.5 py-1.5 rounded-md flex items-center gap-1.5 disabled:opacity-50"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Clear local cache
        </button>
        <div className="text-[11px] text-slate-500 ml-1 inline-flex items-center gap-1">
          <Database className="w-3 h-3" />
          {cacheStats.entries} in-session
        </div>
      </div>

      {scan.running && (
        <>
          <div className="text-[11px] text-slate-400 font-mono">
            {scan.done}/{scan.total} · reported {scan.filled}
          </div>
          <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-[width] duration-300"
              style={{
                width: `${scan.total > 0 ? Math.round((scan.done / scan.total) * 100) : 0}%`,
              }}
            />
          </div>
        </>
      )}

      {!scan.running && scan.result && (
        scan.result.total === 0 ? (
          <div className="text-xs text-emerald-300 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Nothing to report — every movie &amp; series in this playlist
            already has a TMDB id.
          </div>
        ) : (
          <div className="text-xs text-emerald-300 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Sent {scan.result.filled.toLocaleString()} of {scan.result.total.toLocaleString()} to admin.
            They&apos;ll appear under <b>Missing Metadata</b> in the admin
            panel.
          </div>
        )
      )}
    </section>
  );
}



// ---------------------------------------------------------------------------
// TileSizeCard — v1.4.44. User-controlled cover tile size with a live
// preview grid so the user can see exactly how many posters fit per
// row before committing. Wraps getTileSize/setTileSize from
// lib/tileSize.js and dispatches tv_player:tileSize so open Browse /
// Search pages re-render immediately without a refresh.
// ---------------------------------------------------------------------------
function TileSizeCard() {
  const [current, setCurrent] = useState(() => getTileSize());
  const apply = (v) => {
    setTileSize(v);
    setCurrent(v);
  };
  const previewCols = gridClassesFor(current, false);
  const samples = [
    { title: "Movie A", color: "from-orange-500/40 to-red-500/40" },
    { title: "Series B", color: "from-sky-500/40 to-indigo-500/40" },
    { title: "Movie C", color: "from-emerald-500/40 to-teal-500/40" },
    { title: "Series D", color: "from-fuchsia-500/40 to-pink-500/40" },
    { title: "Movie E", color: "from-amber-500/40 to-orange-500/40" },
    { title: "Series F", color: "from-cyan-500/40 to-sky-500/40" },
    { title: "Movie G", color: "from-purple-500/40 to-fuchsia-500/40" },
    { title: "Series H", color: "from-lime-500/40 to-emerald-500/40" },
    { title: "Movie I", color: "from-rose-500/40 to-red-500/40" },
    { title: "Series J", color: "from-blue-500/40 to-cyan-500/40" },
  ];
  return (
    <section className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
          Poster tile size
        </h3>
        <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
          Controls how many posters fit per row in Movies, Series, Search
          and Continue-Watching. Pick a size, then preview it below to
          see how it looks on your screen. Applies the moment you tap a
          size — no refresh needed.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {TILE_SIZES.map((opt) => (
          <button
            key={opt.value}
            data-testid={`tile-size-${opt.value}`}
            onClick={() => apply(opt.value)}
            className={`px-3 py-3 rounded-lg border-2 text-sm font-semibold transition-colors outline-none focus:ring-4 focus:ring-orange-500 ${
              current === opt.value
                ? "border-orange-500 bg-orange-500/15 text-orange-200"
                : "border-white/10 bg-white/5 text-slate-300 hover:border-white/30"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
          Live Preview
        </div>
        <div className="rounded-lg border border-white/5 bg-[#0a0d13] p-3">
          <div className={`grid gap-2 ${previewCols}`}>
            {samples.map((s) => (
              <div
                key={s.title}
                className={`relative aspect-[2/3] rounded-md bg-gradient-to-br ${s.color} border border-white/10 flex items-end p-1.5`}
              >
                <div className="text-[9px] font-semibold text-white/90 leading-tight line-clamp-2">
                  {s.title}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ScreenSizeCard — v1.4.45. Reuses the ScreenSizeSetup dialog body
// (showFrame={false}) as an inline Settings section so users can
// re-adjust the whole-app zoom at any time.
// ---------------------------------------------------------------------------
function ScreenSizeCard() {
  return (
    <section
      data-testid="settings-screen-size"
      className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-4"
    >
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
          Screen size
        </h3>
        <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
          Shrink or grow the entire app to match your TV. Changes apply
          instantly and persist across restarts.
        </p>
      </div>
      <ScreenSizeSetup showFrame={false} />
    </section>
  );
}



function AutoPlayNextEpisodeCard() {
  const [enabled, setEnabled] = useState(() => isAutoPlayNextEpisodeEnabled());

  const toggle = () => {
    const next = !enabled;
    setAutoPlayNextEpisodeEnabled(next);
    setEnabled(next);
  };

  return (
    <section className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
            Auto-play next episode
          </h3>
          <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
            When this is on, tapping an episode pre-loads the rest of the
            season into the native player. The next episode starts the
            moment the current one finishes — no need to come back to the
            list. Disable it if you&apos;d rather pick what plays next
            yourself.
          </p>
        </div>
        <button
          data-testid="settings-autoplay-toggle"
          role="switch"
          aria-checked={enabled}
          onClick={toggle}
          className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${
            enabled ? "bg-emerald-500" : "bg-white/10"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
              enabled ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// DebugModeCard — user-facing toggle for the in-app debug console + remote
// log shipper. Same machinery the "logtest" magic activation key flips on,
// just exposed as a regular setting so end-users can flick it on, reproduce
// a glitch with THEIR playlist, and have the logs appear in the admin panel
// without needing the secret key.
// ---------------------------------------------------------------------------
function DebugModeCard() {
  const [enabled, setEnabled] = useState(() => isDebugEnabled());

  const toggle = () => {
    const next = !enabled;
    if (next) enableDebug();
    else disableDebug();
    setEnabled(next);
  };

  return (
    <section
      data-testid="settings-debug-mode-card"
      className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-3"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-2">
            <Bug className="w-3.5 h-3.5 text-amber-400" />
            Debug mode
          </h3>
          <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
            Turn this on, then reproduce whatever isn&apos;t working with your
            current playlist. The app will quietly ship console logs, errors
            and device info to your provider&apos;s admin panel so they can
            see exactly what happened. Leave it off the rest of the time —
            it costs a little extra bandwidth.
          </p>
          {enabled && (
            <p className="text-[11px] text-amber-300/80 mt-2">
              Logs are being collected. A floating overlay shows the last
              250 entries. Toggle off when you&apos;re done.
            </p>
          )}
        </div>
        <button
          data-testid="settings-debug-mode-toggle"
          role="switch"
          aria-checked={enabled}
          onClick={toggle}
          className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${
            enabled ? "bg-amber-500" : "bg-white/10"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
              enabled ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>
    </section>
  );
}


/**
 * EpgStatusCard — explains in plain English why EPG is or isn't showing.
 *
 * Reads `window.__ZAPP_EPG_DIAG__` which `playlistCache.loadPlaylist`
 * populates on every fresh load with:
 *
 *   { url, tried, ok, error, bytes, programmeCount, channelKeysCount,
 *     sourceChannelMatched, sourceChannelTotal, sampleUnmatched }
 *
 * Three big failure modes the user might hit:
 *
 *   - `tried: false`         → admin didn't set an `epg_url` on this
 *                              playlist AND the M3U didn't declare one
 *                              via `url-tvg`. Fix in admin panel.
 *   - `tried: true, ok: false` → EPG was downloaded but couldn't be parsed
 *                              (gzip not detected, malformed XML, empty
 *                              file). `error` shows the JS exception.
 *   - `programmeCount > 0,    → EPG loaded OK but channel IDs don't match.
 *      sourceChannelMatched=0` Look at `sampleUnmatched` to see what
 *                              tvg-ids our channels have vs what the
 *                              XMLTV declares.
 */
function EpgStatusCard() {
  const { channels, status } = usePlaylistData();
  const [diag, setDiag] = useState(
    typeof window !== "undefined" ? window.__ZAPP_EPG_DIAG__ || null : null,
  );

  // Refresh every 2 s while the playlist is still loading.
  useEffect(() => {
    if (status !== "loading" && diag?.tried) return;
    const t = setInterval(() => {
      setDiag(typeof window !== "undefined" ? window.__ZAPP_EPG_DIAG__ || null : null);
    }, 2000);
    return () => clearInterval(t);
  }, [status, diag?.tried]);

  const liveCount = (channels || []).filter((c) => c.content_type === "live_tv").length;

  let verdict = "Loading…";
  let tone = "neutral";
  if (status !== "loading") {
    if (!diag) {
      verdict = "No EPG attempt yet";
      tone = "neutral";
    } else if (!diag.tried) {
      verdict = "No EPG URL configured for this playlist";
      tone = "warn";
    } else if (diag.error) {
      verdict = `EPG fetch failed: ${diag.error}`;
      tone = "bad";
    } else if (!diag.ok) {
      verdict = "EPG downloaded but parsed 0 programmes";
      tone = "bad";
    } else if (diag.sourceChannelMatched === 0 && diag.sourceChannelTotal > 0) {
      verdict = "EPG loaded — but NO channels matched (tvg-id mismatch)";
      tone = "bad";
    } else if (
      diag.sourceChannelTotal > 0 &&
      diag.sourceChannelMatched < diag.sourceChannelTotal * 0.3
    ) {
      verdict = "EPG loaded — partial match (most channels missing tvg-id)";
      tone = "warn";
    } else {
      verdict = "EPG OK";
      tone = "ok";
    }
  }

  const toneClass =
    tone === "ok"
      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
      : tone === "warn"
      ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
      : tone === "bad"
      ? "bg-rose-500/10 border-rose-500/30 text-rose-300"
      : "bg-white/5 border-white/10 text-white/70";

  const fmtBytes = (n) => {
    if (!n) return "0";
    if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    if (n >= 1024) return `${Math.round(n / 1024)} KB`;
    return `${n} B`;
  };

  return (
    <section
      data-testid="settings-epg-status"
      className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-4"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
          EPG status
        </h3>
        <span
          className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border ${toneClass}`}
        >
          {verdict}
        </span>
      </div>

      {diag?.url && (
        <div className="text-[11px] text-white/50 break-all">
          <span className="text-white/40">URL:</span> {diag.url}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        <div className="bg-black/30 rounded-lg p-2.5">
          <div className="text-white/40 uppercase tracking-wider text-[9px]">
            Downloaded
          </div>
          <div className="font-bold text-white tabular-nums">
            {fmtBytes(diag?.bytes || 0)}
          </div>
        </div>
        <div className="bg-black/30 rounded-lg p-2.5">
          <div className="text-white/40 uppercase tracking-wider text-[9px]">
            Programmes
          </div>
          <div className="font-bold text-white tabular-nums">
            {(diag?.programmeCount || 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-black/30 rounded-lg p-2.5">
          <div className="text-white/40 uppercase tracking-wider text-[9px]">
            EPG channels
          </div>
          <div className="font-bold text-white tabular-nums">
            {(diag?.channelKeysCount || 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-black/30 rounded-lg p-2.5">
          <div className="text-white/40 uppercase tracking-wider text-[9px]">
            Matched
          </div>
          <div className="font-bold text-white tabular-nums">
            {(diag?.sourceChannelMatched || 0).toLocaleString()}
            <span className="text-white/40 font-normal text-[10px]">
              {" / "}
              {(diag?.sourceChannelTotal || liveCount).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {Array.isArray(diag?.sampleUnmatched) && diag.sampleUnmatched.length > 0 && (
        <div className="text-[11px] text-white/55">
          <div className="text-white/40 uppercase tracking-wider text-[9px] mb-1">
            Example unmatched tvg-id / name
          </div>
          <ul className="space-y-0.5 list-disc list-inside text-white/70 font-mono">
            {diag.sampleUnmatched.map((s, i) => (
              <li key={i} className="truncate">{s}</li>
            ))}
          </ul>
          <p className="mt-2 text-[10px] text-white/40 italic">
            These channels in your M3U don&apos;t have a matching{" "}
            <code>&lt;channel id=&quot;…&quot;&gt;</code> in the XMLTV file. Ask your provider
            to align tvg-ids with the EPG, or use a different EPG URL.
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * ImageCacheRow — small stats + clear button for the new image cache.
 * Polls getImageCacheStats() every 2 s so the user can see entries
 * counting up as posters land.
 */
function ImageCacheRow() {
  const [stats, setStats] = useState(() => getImageCacheStats());
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setStats(getImageCacheStats()), 2000);
    return () => clearInterval(t);
  }, []);

  const fmtBytes = (n) => {
    if (!n) return "0 KB";
    if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    if (n >= 1024) return `${Math.round(n / 1024)} KB`;
    return `${n} B`;
  };

  const onClear = async () => {
    if (!window.confirm("Clear cached posters? They will be re-downloaded from your provider as needed.")) return;
    setClearing(true);
    try {
      await clearImageCache();
      setStats(getImageCacheStats());
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="bg-black/30 border border-white/5 rounded-lg p-3 mt-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">
            Poster image cache
          </div>
          <div className="text-[10px] text-white/40 mt-0.5">
            Fetched once from your provider, stored locally for instant repeat loads.
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-bold text-white tabular-nums">
            {stats.entries.toLocaleString()}
          </div>
          <div className="text-[10px] text-white/40">{fmtBytes(stats.bytes)}</div>
        </div>
      </div>
      <button
        data-testid="settings-clear-image-cache"
        onClick={onClear}
        disabled={clearing || stats.entries === 0}
        className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md py-1.5 text-xs disabled:opacity-40"
      >
        {clearing ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Clearing…
          </>
        ) : (
          <>
            <Trash2 className="w-3.5 h-3.5" />
            Clear poster cache
          </>
        )}
      </button>
    </div>
  );
}



/**
 * XtreamCatalogCard — shows live / vod / series counts pulled from the
 * Xtream API for the active playlist. Lets power users compare the
 * numbers against competitor apps (vupro.apk, IPTV Smarters, etc.) and
 * confirm ZA Player Pro discovered the exact same library.
 *
 * Reads `window.__ZAPP_XTREAM_DIAG__` which playlistCache.js populates
 * on every fresh catalog fetch and restores from IndexedDB on cached
 * loads. Polls every 2 s while the playlist is still loading.
 */
function XtreamCatalogCard() {
  const { status } = usePlaylistData();
  const [diag, setDiag] = useState(
    typeof window !== "undefined" ? window.__ZAPP_XTREAM_DIAG__ || null : null,
  );

  useEffect(() => {
    if (status !== "loading" && diag) return;
    const t = setInterval(() => {
      setDiag(typeof window !== "undefined" ? window.__ZAPP_XTREAM_DIAG__ || null : null);
    }, 2000);
    return () => clearInterval(t);
  }, [status, diag]);

  let verdict;
  let tone;
  if (status === "loading") {
    verdict = "Loading catalog…";
    tone = "neutral";
  } else if (!diag) {
    verdict = "No data yet";
    tone = "neutral";
  } else if (diag.skipped) {
    verdict = "Non-Xtream playlist";
    tone = "neutral";
  } else if (diag.error) {
    verdict = "Catalog fetch failed";
    tone = "bad";
  } else if ((diag.live || 0) + (diag.vod || 0) + (diag.series || 0) === 0) {
    verdict = "Empty catalog";
    tone = "warn";
  } else {
    verdict = "Catalog OK";
    tone = "ok";
  }

  const toneClass =
    tone === "ok"
      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
      : tone === "warn"
      ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
      : tone === "bad"
      ? "bg-rose-500/10 border-rose-500/30 text-rose-300"
      : "bg-white/5 border-white/10 text-white/70";

  const skipped = diag?.skipped;

  return (
    <section
      data-testid="settings-xtream-catalog"
      className="bg-[#11141b] border border-white/5 rounded-2xl p-5 space-y-4"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
          Xtream catalog
        </h3>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
          Xtream catalog
        </h3>
        <span
          className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border ${toneClass}`}
        >
          {verdict}
        </span>
      </div>

      <p className="text-[11px] text-white/50 leading-relaxed">
        These are the counts the Xtream <code>player_api.php</code> endpoint
        returned for your provider. Compare them against another player
        (vupro.apk, IPTV Smarters, TiviMate) — the numbers should match. If
        ZA Player Pro shows fewer items than another app, your provider
        is rate-limiting an endpoint; press <em>Reload Playlist</em> on the
        main menu to retry.
      </p>

      {skipped ? (
        <div className="text-[11px] text-white/55">
          {diag?.reason || "This playlist isn't an Xtream Codes panel — catalog metrics aren't available."}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div className="bg-black/30 rounded-lg p-2.5" data-testid="xtream-live-count">
              <div className="text-white/40 uppercase tracking-wider text-[9px]">
                Live channels
              </div>
              <div className="font-bold text-white tabular-nums text-base">
                {(diag?.live || 0).toLocaleString()}
              </div>
              <div className="text-[9px] text-white/40">
                {(diag?.live_categories || 0).toLocaleString()} categories
              </div>
            </div>
            <div className="bg-black/30 rounded-lg p-2.5" data-testid="xtream-vod-count">
              <div className="text-white/40 uppercase tracking-wider text-[9px]">
                Movies (VOD)
              </div>
              <div className="font-bold text-white tabular-nums text-base">
                {(diag?.vod || 0).toLocaleString()}
              </div>
              <div className="text-[9px] text-white/40">
                {(diag?.vod_categories || 0).toLocaleString()} categories
              </div>
            </div>
            <div className="bg-black/30 rounded-lg p-2.5" data-testid="xtream-series-count">
              <div className="text-white/40 uppercase tracking-wider text-[9px]">
                Series
              </div>
              <div className="font-bold text-white tabular-nums text-base">
                {(diag?.series || 0).toLocaleString()}
              </div>
              <div className="text-[9px] text-white/40">
                {(diag?.series_categories || 0).toLocaleString()} categories
              </div>
            </div>
          </div>

          {diag?.error && (
            <div className="text-[11px] text-rose-300/80 bg-rose-500/5 border border-rose-500/20 rounded-md p-2">
              {diag.error}
            </div>
          )}

          {diag?.fetched_at && (
            <div className="text-[10px] text-white/35">
              Last refreshed:{" "}
              {new Date(diag.fetched_at).toLocaleString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
                day: "2-digit",
                month: "short",
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}
